import { addDays, differenceInDays, format, isMonday, parse, setDate, setMonth, setYear } from 'date-fns';

export const ISO_DATE_FORMAT = 'yyyy-MM-dd';
export const ISO_TIME_FORMAT = 'HH:mm';

export const DATE_FORMAT = 'dd-MM-yyyy';
export const DATE_FORMAT_DISPLAY = "EEEE 'd.' d. MMMM, 'kl.' H:mm";
export const DATE_ISO_FORMAT = "yyyy-MM-dd'T'HH:mm:ss";
export const DANISH_DATE_TIME_FORMAT = 'HH:mm dd/MM-yyyy';
export const TIME_FORMAT = 'H:mm';

// According to date-fns, 0 is Sunday
// https://date-fns.org/v2.16.1/docs/getDay
export const weekdays = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

export const mergeDateTime = (date: Date, time: Date | string): Date =>
  setDate(
    setMonth(setYear(typeof time === 'string' ? getDateFromTime(time) : time, date.getFullYear()), date.getMonth()),
    date.getDate()
  );

/**
 * Convert a given time (in string format) into a Date object.
 * @param timeStr Time in the format of HH:mm or HH:mm:ss, e.g. 17:10 or 17:10:59
 */
export const getDateFromTime = (timeStr: string): Date | never => {
  const regex = /^([0-1]\d|2[0-3]):([0-5]\d)(?::([0-5]\d))?$/.exec(timeStr);
  if (!regex) {
    throw new Error(`'${timeStr}' is not a valid time`);
  }

  const time = new Date();
  time.setFullYear(1970, 0, 1);
  time.setHours(parseInt(regex[1], 10), parseInt(regex[2], 10), parseInt(regex[3] ?? 0, 10), 0);

  return time;
};

export const getMondays = (start: Date, end: Date, dateFormat: string): string[] =>
  getDaysByPredicate(start, end, isMonday, dateFormat);

const getDaysByPredicate = (start: Date, end: Date, predicate: (date: Date) => boolean, dateFormat: string) =>
  [...new Array(differenceInDays(end, start) + 1)]
    .map((_, idx) => addDays(start, idx))
    .filter(predicate)
    .map((date) => format(date, dateFormat));

export const getIsoDateFromDanishDate = (danishDate: string): string =>
  format(parse(danishDate, 'dd-MM-yyyy', new Date()), 'yyyy-MM-dd');

export const convertToIsoTime = (time: string) => time.padStart(5, '0');

/**
 * Merge given date and time (in string) into a `Date` object.
 * @param date Date in string with the format dd-MM-yyyy
 * @param time Time in string with the format H:mm
 * @returns `Date` object consolidating the date and time passed in.
 */
export const callStartTime = (date: string, time: string): Date => {
  return mergeDateTime(parse(date, DATE_FORMAT, new Date()), time.padStart(5, '0'));
};

export const getDanishDayName = (date: Date) => {
  const dayNames = ['søndag', 'mandag', 'tirsdag', 'onsdag', 'torsdag', 'fredag', 'lørdag'];

  return dayNames[date.getDay()];
};
